/* =====================================================================
   NOVA — interactions
   Every module initialises only if its markup exists on the page.
   ===================================================================== */
(() => {
  'use strict';
  const S = window.Nova || (window.Nova = {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = window.matchMedia('(pointer: fine)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const root = document.documentElement;

  /* ---------- Intro (logo rises, curtain lifts) ---------- */
  const introEl = document.querySelector('[data-intro]');
  const INTRO_ON = !!introEl && root.classList.contains('has-intro');
  const INTRO_MS = INTRO_ON ? 2600 : 0;
  if (INTRO_ON) {
    document.body.style.overflow = 'hidden';
    let finished = false;
    const finish = () => { if (finished) return; finished = true; document.body.style.overflow = ''; root.classList.add('intro-done'); setTimeout(() => introEl.remove(), 700); };
    setTimeout(finish, 3450);
    introEl.addEventListener('click', () => { introEl.classList.add('is-skipping'); setTimeout(finish, 600); });
  }

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || S.moneyFormat || '€{{amount_with_comma_separator}}';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const withDelims = (n, prec, thou = '.', dec = ',') => {
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(prec);
      const parts = n.split('.');
      const d = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thou);
      return d + (parts[1] ? dec + parts[1] : '');
    };
    const m = fmt.match(re);
    let v = '';
    switch (m ? m[1] : 'amount_with_comma_separator') {
      case 'amount': v = withDelims(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': v = withDelims(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = withDelims(cents, 2, "'", '.'); break;
      default: v = withDelims(cents, 2, '.', ',');
    }
    return fmt.replace(re, v);
  }
  S.formatMoney = formatMoney;

  /* ---------- Storage (safe) ---------- */
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* ignore */ } }
  };

  /* ---------- Colour utils ---------- */
  const hexToRgb = (h) => { h = h.replace('#', ''); if (h.length === 3) h = h.split('').map((c) => c + c).join(''); const n = parseInt(h, 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
  const rgbToHex = (r, g, b) => '#' + [r, g, b].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('');
  const hslToHex = (h, s, l) => {
    s /= 100; l /= 100;
    const k = (n) => (n + h / 30) % 12, a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
  };
  const hexToHsl = (hex) => {
    let [r, g, b] = hexToRgb(hex).map((v) => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0; const l = (max + min) / 2;
    if (max !== min) { const d = max - min; s = l > .5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h *= 60; }
    return [h, s * 100, l * 100];
  };
  const mixHex = (a, b, t) => { const A = hexToRgb(a), B = hexToRgb(b); return rgbToHex(lerp(A[0], B[0], t), lerp(A[1], B[1], t), lerp(A[2], B[2], t)); };

  /* ---------- Global lamp colour (tints the whole site) ---------- */
  const lampListeners = [];
  function setLamp(hex, opts = {}) {
    const [h, s, l] = hexToHsl(hex);
    const second = opts.second || hslToHex((h + 330) % 360, clamp(s, 40, 100), clamp(l - 6, 25, 70));
    root.style.setProperty('--lamp', hex);
    root.style.setProperty('--lamp-2', second);
    S.lamp = { hex, second, scene: opts.scene || 'color', name: opts.name || hex.toUpperCase() };
    if (!opts.silent) store.set('nova-lamp', S.lamp);
    lampListeners.forEach((fn) => fn(S.lamp));
  }
  S.setLamp = setLamp;
  const saved = store.get('nova-lamp');
  if (saved && saved.hex) setLamp(saved.hex, { second: saved.second, scene: saved.scene, name: saved.name, silent: true });

  /* ---------- Split headings into words ---------- */
  function splitWords(el) {
    if (el.dataset.splitDone) return; el.dataset.splitDone = '1';
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            const w = document.createElement('span'); w.className = 'w';
            const inner = document.createElement('span'); inner.style.setProperty('--i', i++); inner.textContent = p;
            w.appendChild(inner); frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && !child.classList.contains('w')) walk(child);
      });
    };
    walk(el);
  }
  $$('[data-split]').forEach(splitWords);

  /* ---------- Reveal on view ---------- */
  const revealIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }) : null;
  $$('.reveal, .reveal-scale, .split').forEach((el) => {
    if (!revealIO || RM) { el.classList.add('is-in'); return; }
    if (!el.classList.contains('is-in')) revealIO.observe(el);
  });
  // headings already on screen: animate right away
  setTimeout(() => $$('.hero .split').forEach((el) => el.classList.add('is-in')), INTRO_ON ? 2750 : 16);

  /* ---------- Counters ---------- */
  function countUp(el) {
    const raw = el.dataset.count; const target = parseFloat(String(raw).replace(',', '.'));
    if (isNaN(target) || RM) return;
    const dur = 1400, t0 = performance.now(), dec = (String(raw).split(/[.,]/)[1] || '').length;
    const tick = (t) => { const k = clamp((t - t0) / dur, 0, 1); const e = 1 - Math.pow(1 - k, 4);
      el.textContent = (target * e).toFixed(dec).replace('.', ','); if (k < 1) requestAnimationFrame(tick); else el.textContent = raw; };
    requestAnimationFrame(tick);
  }
  if ('IntersectionObserver' in window) {
    const cio = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { countUp(e.target); cio.unobserve(e.target); } }), { threshold: .6 });
    $$('[data-count]').forEach((el) => cio.observe(el));
  }

  /* ---------- Header + mobile nav ---------- */
  const header = $('[data-header]');
  const mnav = $('[data-mobile-nav]');
  const menuBtn = $('[data-menu-open]');
  if (menuBtn && mnav) {
    const toggle = (open) => { mnav.classList.toggle('is-open', open); mnav.setAttribute('aria-hidden', String(!open)); menuBtn.setAttribute('aria-expanded', String(open)); document.body.style.overflow = open ? 'hidden' : ''; };
    menuBtn.addEventListener('click', () => toggle(true));
    $('[data-menu-close]', mnav).addEventListener('click', () => toggle(false));
    $$('a', mnav).forEach((a) => a.addEventListener('click', () => toggle(false)));
  }

  /* ---------- Cursor light + magnetic buttons ---------- */
  const cursor = $('[data-cursor-light]');
  if (cursor && FINE && !RM) {
    let cx = innerWidth / 2, cy = innerHeight / 2, tx = cx, ty = cy, running = false;
    const loop = () => { cx = lerp(cx, tx, .14); cy = lerp(cy, ty, .14); cursor.style.transform = `translate(${cx}px, ${cy}px)`;
      if (Math.abs(cx - tx) > .5 || Math.abs(cy - ty) > .5) requestAnimationFrame(loop); else running = false; };
    addEventListener('pointermove', (e) => { tx = e.clientX; ty = e.clientY; root.classList.add('has-pointer'); if (!running) { running = true; requestAnimationFrame(loop); } }, { passive: true });
    document.addEventListener('pointerleave', () => root.classList.remove('has-pointer'));
  }
  if (FINE && !RM) {
    $$('[data-magnetic]').forEach((btn) => {
      btn.addEventListener('pointermove', (e) => { const r = btn.getBoundingClientRect();
        btn.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * .22}px, ${(e.clientY - r.top - r.height / 2) * .32}px)`; });
      btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
    });
  }

  /* ---------- HERO: dark room that switches on ---------- */
  const hero = $('[data-hero]');
  if (hero) {
    const sw = $('[data-hero-switch]', hero);
    const label = $('[data-switch-label]', hero);
    const kelvin = $('[data-kelvin]', hero);
    const setOn = (on) => {
      hero.classList.toggle('is-off', !on);
      if (sw) { sw.setAttribute('aria-checked', String(on)); label.textContent = on ? 'Luce accesa' : 'Luce spenta'; }
      if (kelvin) kelvin.textContent = on ? '≈ 2.100 K' : '—';
    };
    setTimeout(() => setOn(true), RM ? 0 : (INTRO_ON ? 3000 : 450));
    if (sw) sw.addEventListener('click', () => setOn(hero.classList.contains('is-off')));
    const clock = $('[data-clock]', hero);
    const tickClock = () => { if (clock) clock.textContent = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }); };
    tickClock(); setInterval(tickClock, 15000);
    if (FINE && !RM) {
      let gx = 64, gy = 40, tx = 64, ty = 40, raf = 0;
      const glow = () => { gx = lerp(gx, tx, .08); gy = lerp(gy, ty, .08); hero.style.setProperty('--gx', gx + '%'); hero.style.setProperty('--gy', gy + '%');
        raf = (Math.abs(gx - tx) + Math.abs(gy - ty) > .1) ? requestAnimationFrame(glow) : 0; };
      hero.addEventListener('pointermove', (e) => { const r = hero.getBoundingClientRect();
        tx = 40 + ((e.clientX - r.left) / r.width) * 45; ty = 22 + ((e.clientY - r.top) / r.height) * 36; if (!raf) raf = requestAnimationFrame(glow); });
    }
  }

  /* ---------- Big words marquee (speeds up with scroll) ---------- */
  $$('[data-words]').forEach((wrap) => {
    if (RM) return;
    const track = $('.words__track', wrap); let x = 0, vel = 0, visible = false, last = scrollY;
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) requestAnimationFrame(step); }).observe(wrap);
    function step() {
      const dy = scrollY - last; last = scrollY; vel = lerp(vel, Math.abs(dy), .1);
      x -= .6 + vel * .35; const half = track.scrollWidth / 2; if (-x >= half) x += half;
      track.style.transform = `translate3d(${x}px,0,0)`; if (visible) requestAnimationFrame(step);
    }
  });

  /* ---------- Statement: words light up while scrolling ---------- */
  const statements = $$('[data-statement]').map((el) => {
    const hot = (el.dataset.hot || '').toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map((w) => `<span class="sw${hot.includes(w.toLowerCase()) ? ' hot' : ''}">${w.replace(/</g, '&lt;')}</span>`).join(' ');
    return { el, spans: $$('.sw', el) };
  });

  /* ---------- SKY: scroll-driven sunset ---------- */
  const sky = $('[data-sky]');
  const SKY_KEYS = [
    { p: 0, top: '#f2a54e', mid: '#fbcf86', bot: '#ffe6ad', sun: '#ffe08a', wt: '#f6b46c', wb: '#7a3d3a', ink: '#2a1410' },
    { p: .32, top: '#e0663f', mid: '#f38f52', bot: '#ffc07a', sun: '#ffb04a', wt: '#f08a52', wb: '#4a2236', ink: '#2a1008' },
    { p: .58, top: '#5d3577', mid: '#cf4f74', bot: '#f4856a', sun: '#ff5a4a', wt: '#b84a63', wb: '#2a1630', ink: '#fff3ea' },
    { p: .8, top: '#1c1f4e', mid: '#45357a', bot: '#a85a7c', sun: '#c2364f', wt: '#4b3470', wb: '#120c22', ink: '#f6ece2' },
    { p: 1, top: '#07081a', mid: '#12122e', bot: '#251f44', sun: '#7a2240', wt: '#1b1736', wb: '#06050f', ink: '#f6ece2' }
  ];
  const skyAt = (p) => {
    let i = 0; while (i < SKY_KEYS.length - 2 && p > SKY_KEYS[i + 1].p) i++;
    const a = SKY_KEYS[i], b = SKY_KEYS[i + 1], t = clamp((p - a.p) / (b.p - a.p), 0, 1);
    const o = {}; ['top', 'mid', 'bot', 'sun', 'wt', 'wb'].forEach((k) => { o[k] = mixHex(a[k], b[k], t); }); o.ink = t < .5 ? a.ink : b.ink; return o;
  };
  let skyState = null;
  if (sky) {
    const sticky = $('.sky__sticky', sky);
    const clockEl = $('[data-sky-clock]', sky), elevEl = $('[data-sky-elev]', sky), kEl = $('[data-sky-k]', sky);
    const nameEl = $('[data-sky-name]', sky), copyEl = $('[data-sky-copy]', sky), bar = $$('[data-sky-bar] span', sky), end = $('[data-sky-end]', sky);
    const phases = bar.map((s) => ({ name: s.textContent, copy: s.dataset.copy }));
    const canvas = $('[data-stars]', sky);
    const drawStars = () => {
      const dpr = Math.min(devicePixelRatio || 1, 2), w = canvas.clientWidth, h = canvas.clientHeight; if (!w) return;
      canvas.width = w * dpr; canvas.height = h * dpr; const c = canvas.getContext('2d'); c.scale(dpr, dpr);
      let seed = 7; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
      for (let i = 0; i < Math.round(w * h / 2600); i++) { const r = rnd() * 1.3 + .2; c.globalAlpha = .3 + rnd() * .7; c.fillStyle = '#fff'; c.beginPath(); c.arc(rnd() * w, rnd() * h * .95, r, 0, 6.3); c.fill(); }
    };
    drawStars(); addEventListener('resize', drawStars);
    let phase = 0;
    skyState = () => {
      const r = sky.getBoundingClientRect(); const total = sky.offsetHeight - innerHeight;
      const p = RM ? .45 : clamp(-r.top / (total || 1), 0, 1);
      if (r.bottom < 0 || r.top > innerHeight) return;
      const c = skyAt(p); const st = sticky.style;
      st.setProperty('--s-top', c.top); st.setProperty('--s-mid', c.mid); st.setProperty('--s-bot', c.bot);
      st.setProperty('--sun-c', c.sun); st.setProperty('--w-top', c.wt); st.setProperty('--w-bot', c.wb); st.setProperty('--sky-ink', c.ink);
      st.setProperty('--sun-y', lerp(24, 92, Math.pow(p, 1.15)) + '%');
      st.setProperty('--halo', String(clamp(1 - p * 1.2, 0, 1)));
      st.setProperty('--refl', String(clamp(.85 - p * 1.1, 0, .85)));
      st.setProperty('--stars', String(clamp((p - .55) / .35, 0, 1)));
      const endV = clamp((p - .82) / .12, 0, 1); st.setProperty('--end', String(endV)); end.classList.toggle('is-active', endV > .5);
      const mins = 18 * 60 + 30 + Math.round(p * 160);
      clockEl.textContent = String(Math.floor(mins / 60)).padStart(2, '0') + ':' + String(mins % 60).padStart(2, '0');
      const elev = Math.round(lerp(8, -10, p)); elevEl.textContent = (elev > 0 ? '+' : '') + elev + '°';
      kEl.textContent = p < .55 ? '≈ ' + Math.round(lerp(3500, 1900, p / .55) / 100) * 100 + ' K' : p < .85 ? 'blu diffusa' : 'notte';
      const ph = p < .28 ? 0 : p < .55 ? 1 : p < .8 ? 2 : 3;
      if (ph !== phase && phases[ph]) { phase = ph; nameEl.textContent = phases[ph].name; copyEl.textContent = phases[ph].copy; bar.forEach((b, i) => b.classList.toggle('is-on', i <= ph)); }
    };
  }

  /* ---------- Finale sun rises / parallax ---------- */
  const finaleSun = $('[data-finale-sun]');
  const parallax = $$('[data-parallax] img');

  /* ---------- One scroll loop ---------- */
  let ticking = false;
  function onScroll() {
    if (header) header.classList.toggle('is-scrolled', scrollY > 24);
    if (skyState) skyState();
    statements.forEach(({ el, spans }) => {
      const r = el.getBoundingClientRect(); const p = clamp((innerHeight * .85 - r.top) / (r.height + innerHeight * .35), 0, 1);
      const n = Math.round(p * spans.length);
      spans.forEach((s, i) => { const lit = i < n || RM; s.classList.toggle('is-lit', lit && !s.classList.contains('hot')); s.classList.toggle('is-hot', lit && s.classList.contains('hot')); });
    });
    if (finaleSun && !RM) { const r = finaleSun.parentElement.getBoundingClientRect(); const p = clamp((innerHeight - r.top) / (innerHeight * .9), 0, 1); finaleSun.style.transform = `translateY(${(1 - p) * 38}%)`; }
    if (!RM) parallax.forEach((img) => { const r = img.parentElement.getBoundingClientRect(); if (r.bottom < 0 || r.top > innerHeight) return; const k = (r.top + r.height / 2 - innerHeight / 2) / innerHeight; img.style.transform = `scale(1.12) translateY(${k * -6}%)`; });
    ticking = false;
  }
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
  addEventListener('resize', onScroll); onScroll();

  /* ---------- COLOUR LAB ---------- */
  $$('[data-lab]').forEach((lab) => {
    const wall = $('[data-wall]', lab), wheel = $('[data-wheel]', lab), dot = $('[data-wheel-dot]', lab), hexEl = $('[data-hex]', lab);
    const readout = $('[data-readout]', lab), sceneName = $('[data-scene-name]', lab), chips = $$('[data-chips] .chip', lab);
    const canvas = $('canvas', wheel); const ctx = canvas.getContext('2d');
    const W = canvas.width, R = W / 2;
    // draw wheel
    if (ctx.createConicGradient) {
      const g = ctx.createConicGradient(-Math.PI / 2, R, R);
      for (let i = 0; i <= 12; i++) g.addColorStop(i / 12, `hsl(${i * 30} 100% 55%)`);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(R, R, R, 0, Math.PI * 2); ctx.fill();
    } else {
      for (let a = 0; a < 360; a++) { ctx.fillStyle = `hsl(${a},100%,55%)`; ctx.beginPath(); ctx.moveTo(R, R); ctx.arc(R, R, R, (a - 91) * Math.PI / 180, (a - 89) * Math.PI / 180); ctx.fill(); }
    }
    const rg = ctx.createRadialGradient(R, R, R * .15, R, R, R); rg.addColorStop(0, 'rgba(255,255,255,1)'); rg.addColorStop(.55, 'rgba(255,255,255,.15)'); rg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(R, R, R, 0, Math.PI * 2); ctx.fill();

    const placeDot = (h, s) => { const rad = (h - 90) * Math.PI / 180; const d = clamp((s - 25) / 75, 0, 1) * .34 + .62; dot.style.left = 50 + Math.cos(rad) * d * 50 + '%'; dot.style.top = 50 + Math.sin(rad) * d * 50 + '%'; wheel.setAttribute('aria-valuenow', Math.round(h)); };
    const setMode = (mode) => { wall.classList.toggle('is-sunset', mode === 'sunset'); wall.classList.toggle('is-rainbow', mode === 'rainbow'); wall.classList.remove('is-off'); };
    const label = (name) => { readout.textContent = name; sceneName.textContent = name; };
    const pressChip = (el) => chips.forEach((c) => c.setAttribute('aria-pressed', String(c === el)));

    lampListeners.push((l) => { hexEl.textContent = l.hex.toUpperCase(); const [h, s] = hexToHsl(l.hex); placeDot(h, s); });
    const applyColor = (hex, name, chip) => { setMode('color'); setLamp(hex, { name }); label(name || hex.toUpperCase()); pressChip(chip); };
    const applyScene = (scene, chip) => {
      if (scene === 'sunset') { setMode('sunset'); setLamp('#ff8a3d', { second: '#f2557a', scene: 'sunset', name: 'Tramonto' }); label('Tramonto'); }
      else if (scene === 'rainbow') { setMode('rainbow'); setLamp('#ff4fd8', { scene: 'rainbow', name: 'Arcobaleno' }); label('Arcobaleno'); }
      else if (scene === 'random') { applyColor(hslToHex(Math.random() * 360, 95, 58), null, null); return; }
      else if (scene === 'flash') { const seq = ['#ff2d2d', '#ffd23f', '#2fd25b', '#2f6bff', '#b44cff', '#ff8a3d']; seq.forEach((c, i) => setTimeout(() => { setMode('color'); setLamp(c, { name: 'Flash' }); label('Flash'); }, RM ? 0 : i * 160)); }
      pressChip(chip || null);
    };
    // initial state from saved lamp
    if (S.lamp && S.lamp.scene !== 'sunset') { if (S.lamp.scene === 'rainbow') applyScene('rainbow', chips.find((c) => c.dataset.scene === 'rainbow')); else { setMode('color'); label(S.lamp.name || S.lamp.hex.toUpperCase()); pressChip(chips.find((c) => c.dataset.color === S.lamp.hex)); } }
    { const l = S.lamp || { hex: getComputedStyle(root).getPropertyValue('--lamp').trim() || '#ff8a3d' }; hexEl.textContent = l.hex.toUpperCase(); const [h, s] = hexToHsl(l.hex); placeDot(h, s); }

    // wheel drag
    const pick = (e) => { const r = wheel.getBoundingClientRect(); const x = e.clientX - r.left - r.width / 2, y = e.clientY - r.top - r.height / 2;
      const h = (Math.atan2(y, x) * 180 / Math.PI + 90 + 360) % 360; const s = clamp(Math.hypot(x, y) / (r.width / 2), 0, 1); const sat = clamp(25 + ((s - .62) / .34) * 75, 25, 100);
      applyColor(hslToHex(h, sat, 56), null, null); };
    let dragging = false;
    wheel.addEventListener('pointerdown', (e) => { dragging = true; wheel.setPointerCapture(e.pointerId); pick(e); });
    wheel.addEventListener('pointermove', (e) => { if (dragging) pick(e); });
    wheel.addEventListener('pointerup', () => { dragging = false; });
    wheel.addEventListener('keydown', (e) => { if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return; e.preventDefault();
      const [h, s] = hexToHsl(S.lamp ? S.lamp.hex : '#ff8a3d'); const step = (e.key === 'ArrowRight' || e.key === 'ArrowUp') ? 6 : -6; applyColor(hslToHex((h + step + 360) % 360, Math.max(s, 70), 56), null, null); });

    // chips + remote
    $$('[data-color], [data-scene]', lab).forEach((b) => b.addEventListener('click', () => {
      b.classList.add('is-press'); setTimeout(() => b.classList.remove('is-press'), 140);
      const isChip = b.classList.contains('chip');
      if (b.dataset.scene) applyScene(b.dataset.scene, isChip ? b : null);
      else applyColor(b.dataset.color, isChip ? b.textContent.trim() : null, isChip ? b : null);
    }));
    $$('[data-power]', lab).forEach((b) => b.addEventListener('click', () => { wall.classList.toggle('is-off', b.dataset.power === 'off'); label(b.dataset.power === 'off' ? 'Spenta' : (S.lamp ? S.lamp.name : 'Tramonto')); }));
    // tabs
    const tabs = $$('[data-tab]', lab), panes = $$('[data-pane]', lab);
    tabs.forEach((t, i) => t.addEventListener('click', () => { tabs.forEach((x, j) => x.setAttribute('aria-selected', String(i === j))); panes.forEach((p, j) => { p.hidden = i !== j; }); }));
    tabs.forEach((t, i) => t.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]; n.focus(); n.click(); } }));
  });

  /* ---------- Before / after ---------- */
  $$('[data-ba]').forEach((ba) => {
    const range = $('[data-ba-range]', ba); const set = (v) => ba.style.setProperty('--pos', v + '%');
    range.addEventListener('input', () => set(range.value));
    if (!RM && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([e]) => { if (!e.isIntersecting) return; io.disconnect();
        const keys = [[0, 50], [700, 78], [1500, 24], [2300, 50]]; const t0 = performance.now();
        const anim = (t) => { const dt = t - t0; let i = 0; while (i < keys.length - 2 && dt > keys[i + 1][0]) i++;
          const [ta, va] = keys[i], [tb, vb] = keys[i + 1]; const k = clamp((dt - ta) / (tb - ta), 0, 1); const e2 = k < .5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
          const v = lerp(va, vb, e2); set(v); range.value = v; if (dt < keys[keys.length - 1][0]) requestAnimationFrame(anim); };
        requestAnimationFrame(anim); }, { threshold: .5 });
      io.observe(ba);
    }
  });

  /* ---------- Rail: arrows, drag, tilt ---------- */
  $$('[data-rail]').forEach((rail) => {
    const sec = rail.closest('section'); const step = () => (rail.firstElementChild ? rail.firstElementChild.getBoundingClientRect().width + 18 : 300);
    const prev = $('[data-rail-prev]', sec), next = $('[data-rail-next]', sec);
    if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    let down = false, sx = 0, sl = 0, moved = false;
    rail.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') return; down = true; moved = false; sx = e.clientX; sl = rail.scrollLeft; });
    addEventListener('pointermove', (e) => { if (!down) return; const dx = e.clientX - sx; if (Math.abs(dx) > 4) { moved = true; rail.classList.add('is-drag'); } rail.scrollLeft = sl - dx; });
    addEventListener('pointerup', () => { if (!down) return; down = false; rail.classList.remove('is-drag'); });
    rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
    if (FINE && !RM) $$('[data-tilt]', rail).forEach((card) => {
      card.addEventListener('pointermove', (e) => { const r = card.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width - .5, y = (e.clientY - r.top) / r.height - .5;
        card.style.transform = `perspective(900px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg)`; });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  });

  /* ---------- 360° orbit ---------- */
  $$('[data-orbit]').forEach((orbit) => {
    const head = $('.orbit__head', orbit), beam = $('.orbit__beam', orbit), lens = $('.lens', orbit);
    const sec = orbit.closest('section'); const ay = $('[data-angle-y]', sec), ax = $('[data-angle-x]', sec);
    let ry = 0, rx = 0, try_ = 0, trx = 0, dragging = false, touched = false, px = 0, py = 0;
    if (head) { head.style.transformBox = 'fill-box'; head.style.transformOrigin = '50% 50%'; }
    const render = () => {
      ry = lerp(ry, try_, .12); rx = lerp(rx, trx, .12);
      const cy = Math.cos(ry * Math.PI / 180), cx = Math.cos(rx * Math.PI / 180);
      if (head) head.style.transform = `scale(${Math.max(Math.abs(cy), .08) * (cy < 0 ? -1 : 1)}, ${Math.max(cx, .2)}) skewY(${Math.sin(ry * Math.PI / 180) * -6}deg)`;
      if (lens) lens.style.opacity = cy < 0 ? '0.08' : '1';
      beam.style.setProperty('--bx', Math.sin(ry * Math.PI / 180) * 34 + '%'); beam.style.setProperty('--by', Math.sin(rx * Math.PI / 180) * -40 + '%');
      beam.style.opacity = String(clamp(cy, 0, 1));
      const deg = ((Math.round(ry) % 360) + 360) % 360; ay.textContent = deg + '°'; ax.textContent = Math.round(rx) + '°';
      requestAnimationFrame(render);
    };
    requestAnimationFrame(render);
    orbit.addEventListener('pointerdown', (e) => { dragging = touched = true; px = e.clientX; py = e.clientY; orbit.setPointerCapture(e.pointerId); });
    orbit.addEventListener('pointermove', (e) => { if (!dragging) return; try_ += (e.clientX - px) * .7; trx = clamp(trx - (e.clientY - py) * .4, -55, 55); px = e.clientX; py = e.clientY; });
    orbit.addEventListener('pointerup', () => { dragging = false; });
    orbit.addEventListener('keydown', (e) => { const m = { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, 6], ArrowDown: [0, -6] }[e.key]; if (!m) return; e.preventDefault(); touched = true; try_ += m[0]; trx = clamp(trx + m[1], -55, 55); });
    if (!RM) { let t = 0; const idle = () => { if (!touched) { t += .012; try_ = Math.sin(t) * 38; trx = Math.sin(t * .7) * 12; requestAnimationFrame(idle); } }; idle(); }
  });

  /* ---------- Lightbox + toast ---------- */
  const lb = $('[data-lightbox]');
  const openLightbox = (src, alt) => { if (!lb) return; const img = $('img', lb); img.src = src; img.alt = alt || ''; lb.classList.add('is-open'); lb.setAttribute('aria-hidden', 'false'); $('[data-lightbox-close]', lb).focus(); };
  if (lb) { const close = () => { lb.classList.remove('is-open'); lb.setAttribute('aria-hidden', 'true'); };
    lb.addEventListener('click', (e) => { if (e.target === lb || e.target.closest('[data-lightbox-close]')) close(); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); }); }
  const toastEl = $('[data-toast]'); let toastT;
  const toast = (msg) => { if (!toastEl) return; toastEl.textContent = msg; toastEl.classList.add('is-on'); clearTimeout(toastT); toastT = setTimeout(() => toastEl.classList.remove('is-on'), 2600); };

  /* =====================================================================
     CART (AJAX) + DRAWER
     ===================================================================== */
  const drawer = $('[data-drawer]');
  const preview = !!S.preview;
  const mock = { items: [], item_count: 0, total_price: 0 };
  const recalcMock = () => { mock.item_count = mock.items.reduce((a, i) => a + i.quantity, 0); mock.total_price = mock.items.reduce((a, i) => a + i.final_line_price, 0); return mock; };
  const api = {
    async get() { if (preview) return recalcMock(); const r = await fetch(S.routes.cart + '.js', { headers: { Accept: 'application/json' } }); return r.json(); },
    async add(fd, meta) {
      if (preview) { const id = String(fd.get('id')); const q = parseInt(fd.get('quantity') || '1', 10); const ex = mock.items.find((i) => String(i.id) === id);
        if (ex) { ex.quantity += q; ex.final_line_price = ex.price * ex.quantity; } else mock.items.push({ id, key: id, quantity: q, price: meta.price, final_line_price: meta.price * q, product_title: meta.title, variant_title: meta.variant, image: meta.image, url: '#' });
        return recalcMock(); }
      const r = await fetch(S.routes.cartAdd + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const j = await r.json(); if (!r.ok || j.status) throw new Error(j.description || j.message || 'Impossibile aggiungere al carrello'); return j;
    },
    async change(line, quantity) {
      if (preview) { const it = mock.items[line - 1]; if (it) { if (quantity <= 0) mock.items.splice(line - 1, 1); else { it.quantity = quantity; it.final_line_price = it.price * quantity; } } return recalcMock(); }
      const r = await fetch(S.routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      return r.json();
    }
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  function renderCart(cart) {
    $$('[data-cart-count]').forEach((c) => { c.textContent = cart.item_count; c.hidden = !cart.item_count; c.classList.remove('bump'); void c.offsetWidth; c.classList.add('bump'); });
    if (!drawer) return;
    const items = $('[data-drawer-items]', drawer), foot = $('[data-drawer-foot]', drawer);
    if (!cart.item_count) {
      items.innerHTML = `<div class="drawer__empty"><span class="logo__sun"></span><p>Il carrello è vuoto. Il tramonto ti aspetta.</p><a class="btn btn--ghost" href="${esc((S.routes && S.routes.root) || '/')}">Scopri la lampada</a></div>`;
      foot.hidden = true;
    } else {
      items.innerHTML = cart.items.map((it, i) => {
        const img = it.image ? (it.image.indexOf('?') > -1 || preview ? it.image : it.image + '&width=200') : '';
        return `<div class="line">
          <a class="line__img" href="${esc(it.url)}">${img ? `<img src="${esc(img)}" alt="">` : ''}</a>
          <div><a class="line__title" href="${esc(it.url)}">${esc(it.product_title)}</a>
            ${it.variant_title && it.variant_title !== 'Default Title' ? `<div class="line__variant">${esc(it.variant_title)}</div>` : ''}
            <div class="qty"><button type="button" data-line="${i + 1}" data-q="${it.quantity - 1}" aria-label="Diminuisci">−</button><input type="number" value="${it.quantity}" readonly aria-label="Quantità"><button type="button" data-line="${i + 1}" data-q="${it.quantity + 1}" aria-label="Aumenta">+</button></div>
            <button class="line__remove" type="button" data-line="${i + 1}" data-q="0">Rimuovi</button>
          </div>
          <div class="line__price">${formatMoney(it.final_line_price)}</div></div>`;
      }).join('');
      foot.hidden = false; $('[data-drawer-total]', drawer).textContent = formatMoney(cart.total_price);
    }
    const fs = $('[data-freeship]', drawer), th = S.freeShipCents || 0;
    if (fs) { const txt = $('[data-freeship-text]', fs), barEl = $('.freeship__bar', fs);
      if (th > 0) { const left = th - cart.total_price; fs.style.setProperty('--fs', clamp(cart.total_price / th * 100, 0, 100) + '%');
        txt.innerHTML = left > 0 ? `Ti mancano <b>${formatMoney(left)}</b> per la spedizione gratuita` : '<b>Spedizione gratuita</b> sbloccata'; }
      else { fs.style.setProperty('--fs', '100%'); txt.innerHTML = '<b>Spedizione gratuita</b> inclusa in tutta Italia'; }
      barEl.hidden = false; }
  }
  let lastFocus = null;
  function openDrawer() { if (!drawer) return; lastFocus = document.activeElement; drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; setTimeout(() => $('.drawer__panel', drawer).focus(), 50); }
  function closeDrawer() { if (!drawer) return; drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; if (lastFocus) lastFocus.focus(); }
  if (drawer) {
    $$('[data-drawer-close]', drawer).forEach((b) => b.addEventListener('click', closeDrawer));
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
    drawer.addEventListener('click', async (e) => { const b = e.target.closest('[data-line]'); if (!b) return; b.disabled = true; renderCart(await api.change(+b.dataset.line, Math.max(0, +b.dataset.q))); });
    $$('[data-cart-open]').forEach((a) => a.addEventListener('click', async (e) => { e.preventDefault(); renderCart(await api.get()); openDrawer(); }));
    api.get().then((c) => { if (c && c.item_count) renderCart(c); }).catch(() => {});
  }
  S.openCart = async () => { renderCart(await api.get()); openDrawer(); };

  /* =====================================================================
     PRODUCT PAGE
     ===================================================================== */
  $$('[data-pdp]').forEach((pdp) => {
    // gallery
    const track = $('[data-track]', pdp), slides = $$('[data-slide]', pdp), thumbs = $$('[data-thumb]', pdp), countLbl = $('[data-count-label]', pdp);
    const go = (i) => track.scrollTo({ left: slides[i].offsetLeft, behavior: RM ? 'auto' : 'smooth' });
    thumbs.forEach((t) => t.addEventListener('click', () => go(+t.dataset.thumb)));
    let gT; track.addEventListener('scroll', () => { clearTimeout(gT); gT = setTimeout(() => { const i = Math.round(track.scrollLeft / track.clientWidth);
      thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j))); if (countLbl) countLbl.textContent = (i + 1) + ' / ' + slides.length;
      if (thumbs[i]) thumbs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' }); }, 60); }, { passive: true });
    track.addEventListener('keydown', (e) => { const i = Math.round(track.scrollLeft / track.clientWidth); if (e.key === 'ArrowRight') go(Math.min(i + 1, slides.length - 1)); if (e.key === 'ArrowLeft') go(Math.max(i - 1, 0)); });
    slides.forEach((sl) => {
      sl.addEventListener('click', () => openLightbox(sl.dataset.full, $('img:not(.gallery__blur)', sl).alt));
      if (FINE) { sl.addEventListener('pointermove', (e) => { const r = sl.getBoundingClientRect(); sl.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%'); sl.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%'); });
        sl.addEventListener('pointerenter', () => sl.classList.add('is-zoom')); sl.addEventListener('pointerleave', () => sl.classList.remove('is-zoom')); }
    });

    // variants + price
    let variants = []; try { variants = JSON.parse($('[data-product-json]', pdp).textContent); } catch (e) { /* none */ }
    const idInput = $('[data-variant-id]', pdp), sel = $('[data-variant-select]', pdp);
    const priceNow = $('[data-price-now]', pdp), priceWas = $('[data-price-was]', pdp), priceSave = $('[data-price-save]', pdp), stickyPrice = $('[data-sticky-price]', pdp);
    const atc = $('[data-atc]', pdp), atcLabel = $('[data-atc-label]', pdp); const atcText = atcLabel ? atcLabel.textContent : '';
    let current = variants.find((v) => String(v.id) === String(idInput && idInput.value)) || variants[0];
    const bundles = $$('[data-bundles] input', pdp); const qtyInput = $('[data-qty-input]', pdp);
    const updateBundles = () => { if (!current) return; bundles.forEach((b) => { const q = +b.dataset.qty, d = +b.dataset.discount || 0; const full = current.price * q; const lbl = b.closest('.bundle');
      $('[data-bundle-final]', lbl).textContent = formatMoney(Math.round(full * (100 - d) / 100)); const s = $('[data-bundle-full]', lbl); if (s) s.textContent = formatMoney(full); }); };
    const updateVariant = (v) => {
      current = v; if (!v) return; if (idInput) idInput.value = v.id;
      if (priceNow) priceNow.textContent = formatMoney(v.price); if (stickyPrice) stickyPrice.textContent = formatMoney(v.price);
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (priceWas) { priceWas.hidden = !onSale; if (onSale) priceWas.textContent = formatMoney(v.compare_at_price); }
      if (priceSave) { priceSave.hidden = !onSale; if (onSale) priceSave.textContent = 'Risparmi ' + Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '%'; }
      if (atc) { atc.disabled = !v.available; atcLabel.textContent = v.available ? atcText : 'Esaurito'; }
      updateBundles();
      if (!preview && history.replaceState) { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u); }
    };
    if (sel) sel.addEventListener('change', () => updateVariant(variants.find((v) => String(v.id) === sel.value)));
    bundles.forEach((b) => b.addEventListener('change', () => { if (qtyInput) qtyInput.value = b.dataset.qty; }));
    updateBundles();

    // qty
    const qtyWrap = $('[data-qty-wrap]', pdp);
    if (qtyWrap) { const syncBundle = () => bundles.forEach((b) => { b.checked = +b.dataset.qty === +qtyInput.value; });
      $('[data-qty-minus]', qtyWrap).addEventListener('click', () => { qtyInput.value = Math.max(1, (+qtyInput.value || 1) - 1); syncBundle(); });
      $('[data-qty-plus]', qtyWrap).addEventListener('click', () => { qtyInput.value = (+qtyInput.value || 1) + 1; syncBundle(); });
      qtyInput.addEventListener('change', syncBundle); }

    // add to cart
    const form = $('form.product-form', pdp), err = $('[data-form-error]', pdp);
    if (form) form.addEventListener('submit', async (e) => {
      e.preventDefault(); if (!atc || atc.disabled) return; atc.classList.add('is-loading'); if (err) err.hidden = true;
      const fd = new FormData(form); $$('[data-bundles] input', pdp).forEach((b) => fd.delete(b.name));
      try {
        const meta = { price: current ? current.price : 0, title: pdp.dataset.title, variant: current ? current.title : '', image: pdp.dataset.image };
        await api.add(fd, meta); const cart = await api.get(); renderCart(cart); openDrawer();
      } catch (ex) { if (err) { err.textContent = ex.message; err.hidden = false; } toast(ex.message); }
      finally { atc.classList.remove('is-loading'); }
    });

    // sticky bar
    const sticky = $('[data-sticky-atc]', pdp);
    if (sticky && atc && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => { const show = !e.isIntersecting && e.boundingClientRect.top < 0; sticky.classList.toggle('is-visible', show); sticky.setAttribute('aria-hidden', String(!show));
        $('[data-sticky-buy]', sticky).tabIndex = show ? 0 : -1; }).observe(atc);
      $('[data-sticky-buy]', sticky).addEventListener('click', () => { if (form.requestSubmit) form.requestSubmit(); else atc.click(); });
    }

    // delivery estimate (business days)
    const addBiz = (d, n) => { const x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); if (x.getDay() !== 0 && x.getDay() !== 6) n--; } return x; };
    const fmt = (d) => d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    const now = new Date(); const d1 = $('[data-ship-d1]', pdp), d2 = $('[data-ship-d2]', pdp);
    if (d1) d1.textContent = fmt(addBiz(now, 1));
    if (d2) d2.textContent = fmt(addBiz(now, S.shipMin || 3)) + '–' + fmt(addBiz(now, S.shipMax || 6));
  });

  /* ---------- Preview-only: keep forms on the page ---------- */
  if (preview) $$('form').forEach((f) => { if (f.classList.contains('product-form')) return; f.addEventListener('submit', (e) => { e.preventDefault(); const m = f.parentElement.querySelector('[data-news-msg]'); if (m) m.hidden = false; toast('Anteprima: il modulo funziona sul negozio Shopify'); }); });
})();
